import { useState } from "react";
import { Bookmark, ArrowUpRight } from "lucide-react";
import ForensicQuoteArtifact from "@/components/ForensicQuoteArtifact";
import type { ResonantMoment } from "@/lib/resonantMoments";

interface Props {
  moments: ResonantMoment[];
}

/**
 * Resonant Moments — surfaces emotionally precise excerpts as
 * archival forensic cards. Quiet, recognizable, premium.
 */
const ResonantMomentsSection = ({ moments }: Props) => {
  const [active, setActive] = useState<ResonantMoment | null>(null);
  const [open, setOpen] = useState(false);

  if (!moments.length) return null;

  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="w-1 h-1 rounded-full bg-cyan/60" />
        <p className="text-[10px] font-mono tracking-[0.28em] uppercase text-muted-foreground/70">
          Resonant Moments
        </p>
      </div>

      <p className="text-[10px] text-muted-foreground/45 italic font-light">
        Phrases recognized across emotionally familiar conflict patterns.
      </p>

      <div className="space-y-2.5 pt-1">
        {moments.map((m, i) => (
          <button
            key={i}
            onClick={() => {
              setActive(m);
              setOpen(true);
            }}
            className="w-full text-left group rounded-xl border border-border/40 bg-card/40 backdrop-blur-sm p-4 transition-all hover:border-cyan/30 hover:bg-card/60"
          >
            <div className="flex items-center justify-between mb-2">
              <p className="text-[9px] font-mono tracking-[0.26em] uppercase text-cyan/60">
                {m.label}
              </p>
              <ArrowUpRight className="w-3.5 h-3.5 text-muted-foreground/40 group-hover:text-cyan/80 transition-colors" />
            </div>
            <p className="text-sm text-foreground/85 font-light leading-snug italic">
              "{m.quote}"
            </p>
            <p className="text-[10px] text-muted-foreground/55 mt-2 leading-relaxed font-light">
              {m.observation}
            </p>
            <div className="flex items-center gap-1.5 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
              <Bookmark className="w-3 h-3 text-cyan/70" />
              <span className="text-[9px] font-mono tracking-[0.24em] uppercase text-cyan/70">
                Preserve as forensic card
              </span>
            </div>
          </button>
        ))}
      </div>

      <ForensicQuoteArtifact moment={active} open={open} onOpenChange={setOpen} />
    </section>
  );
};

export default ResonantMomentsSection;
